import React from "react"
import { NavLink, useLocation, useNavigate } from "react-router-dom"
import axios from "axios"
import {
  Sidebar as SidebarRoot,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar"
import { useAuth } from "@/contexts/AuthContext"
import { useTheme } from "@/contexts/ThemeContext"
import { useCompanyLogo } from "@/hooks/useCompanyLogo"
import { getAppSocket } from "@/lib/wesSocket"
import { WHATSAPP_CHAT_ENABLED } from "@/config/modules"
import {
  ChevronRightIcon,
  ChartBarIcon,
  CreditCardIcon,
  CubeIcon,
  ShoppingCartIcon,
  Squares2X2Icon,
  Cog6ToothIcon,
  QueueListIcon,
  MegaphoneIcon,
  WrenchScrewdriverIcon,
  ArrowTopRightOnSquareIcon,
  ArrowsUpDownIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  BookOpenIcon,
} from "@heroicons/react/24/outline"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"

type SubNavigationItem = {
  name: string
  href: string
  shortcut?: string
  permission?: string | null
  anyPermissions?: string[]
  hideForRoles?: string[]
  adminOnly?: boolean
  toggleKey?: string
  external?: boolean
}

type NavigationItem = {
  name: string
  href?: string
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  permission?: string | null
  anyPermissions?: string[]
  adminOnly?: boolean
  shortcut?: string
  toggleKey?: string
  items?: SubNavigationItem[]
}

const navigation: NavigationItem[] = [
  // ── OPERACIÓN: lo que pasa hoy ──────────────────────────────
  {
    name: "Operacion",
    icon: Squares2X2Icon,
    items: [
      { name: "Dashboard",  href: "/dashboard",              permission: "dashboard" },
      { name: "Turnos",     href: "/appointments-enhanced",  permission: "appointments", shortcut: "T" },
      { name: "Inicio",     href: "/inicio",                 permission: "wes_operator", hideForRoles: ["admin"] },
      { name: "Taller", href: "/taller",   anyPermissions: ["reports", "wes_general", "wes_operator"] },
      // Módulo apagado (WHATSAPP_CHAT_ENABLED): se filtra abajo, no se borra
      { name: "Chat",       href: "/chat",                   permission: "whatsapp", shortcut: "W" },
      { name: "Tareas",     href: "/tasks", toggleKey: "tareas" },
    ],
  },
  // ── REDES SOCIALES ──────────────────────────────────────────
  {
    name: "Redes",
    icon: MegaphoneIcon,
    anyPermissions: ["manage_content_calendar", "reports"],
    items: [
      { name: "Calendario de contenido", href: "/content-calendar", permission: "manage_content_calendar" },
      { name: "Banco de ideas",           href: "/content-ideas",    permission: "manage_content_calendar" },
      { name: "Eventos (Google)",         href: "/content-events",   permission: "manage_content_calendar" },
      { name: "Banco de medios",          href: "/content-media",    permission: "manage_content_calendar" },
      { name: "Reseñas de Google",        href: "/reviews",          permission: "reports" },
    ],
  },
  // ── COMERCIAL: el proceso de venta ──────────────────────────
  {
    name: "Comercial",
    icon: ShoppingCartIcon,
    anyPermissions: ["clients", "reports", "invoices"],
    items: [
      { name: "Clientes",        href: "/clients",       permission: "clients" },
      { name: "Presupuestos",    href: "/budgets",       permission: "reports", shortcut: "P" },
      { name: "Diagnósticos",    href: "/diagnostics",   anyPermissions: ["reports", "diagnostics"], shortcut: "D" },
      { name: "Pendientes",      href: "/service-status", anyPermissions: ["reports", "diagnostics"] },
      { name: "Remitos",         href: "/remitos",       permission: "reports", shortcut: "R" },
      { name: "Facturas",        href: "/invoices",      anyPermissions: ["reports", "invoices"], shortcut: "F" },
      { name: "Notas de Crédito",href: "/notas-credito", anyPermissions: ["reports", "invoices"] },
    ],
  },
  // ── FINANZAS ────────────────────────────────────────────────
  {
    name: "Finanzas",
    icon: CreditCardIcon,
    anyPermissions: ["reports", "current_accounts", "manage_staff_payments"],
    items: [
      { name: "Resumen del día",   href: "/resumen-diario",       permission: "reports" },
      { name: "Caja",              href: "/cash-register",        anyPermissions: ["reports", "finances"], shortcut: "C" },
      { name: "Posición",          href: "/finanzas/posicion",    anyPermissions: ["reports", "finances"] },
      { name: "Cheques",           href: "/cash-register/checks", anyPermissions: ["reports", "finances"] },
      { name: "Ctas. Corrientes",  href: "/current-accounts",     anyPermissions: ["reports", "finances", "current_accounts"] },
      { name: "Cuentas",           href: "/cuentas",              anyPermissions: ["reports", "finances"] },
      { name: "Gastos operativos", href: "/operating-expenses",   anyPermissions: ["reports", "finances"] },
      { name: "Pagos al personal", href: "/staff-payments",       permission: "manage_staff_payments" },
    ],
  },
  // ── INVENTARIO ──────────────────────────────────────────────
  {
    name: "Inventario",
    icon: CubeIcon,
    anyPermissions: ["products", "supplies", "providers", "request_supplies", "approve_supply_requests", "reports", "manage_assets"],
    items: [
      { name: "Productos y Servicios", href: "/products",        permission: "products", hideForRoles: ["operario"] },
      { name: "Equipos y herramientas", href: "/assets",         permission: "manage_assets" },
      { name: "Insumos",               href: "/supplies",        permission: "supplies" },
      { name: "Pedidos internos",      href: "/supply-requests", anyPermissions: ["request_supplies", "approve_supply_requests", "supplies"] },
      { name: "Proveedores",           href: "/providers",       permission: "providers" },
      { name: "Compras",               href: "/purchases",       anyPermissions: ["reports", "finances"] },
      { name: "Notas de crédito (compra)", href: "/purchases/credit-notes", anyPermissions: ["reports", "finances", "providers"] },
      { name: "Listas de precios",     href: "/price-lists",     permission: "products", hideForRoles: ["operario"] },
    ],
  },
  // ── ANÁLISIS ────────────────────────────────────────────────
  {
    name: "Análisis",
    icon: ChartBarIcon,
    anyPermissions: ["reports"],
    items: [
      { name: "Reportes",     href: "/reports",    permission: "reports" },
      { name: "Marketing",    href: "/marketing",  adminOnly: true, toggleKey: "marketing" },
      { name: "Fidelización", href: "/loyalty",    permission: "reports", toggleKey: "fidelizacion" },
    ],
  },
  // ── SISTEMA ─────────────────────────────────────────────────
  // Agente IA quitado del menu (seccion desactivada); la pagina sigue en /ai-agent
  { name: "Documentos",   href: "/manuals",   icon: BookOpenIcon,  anyPermissions: ["view_manuals", "manage_manuals"] },
  { name: "Configuracion",href: "/settings",  icon: Cog6ToothIcon, adminOnly: true },
]

const AppSidebar: React.FC = () => {
  const { user, hasPermission, hasRole } = useAuth()
  const { isDarkMode } = useTheme()
  const { getLogoUrl, getLogoDarkUrl } = useCompanyLogo()
  const location = useLocation()
  const navigate = useNavigate()
  const { state } = useSidebar()
  const [openMenus, setOpenMenus] = React.useState<Record<string, boolean>>({})
  const [chatUnreadCount, setChatUnreadCount] = React.useState(0)
  const [sectionToggles, setSectionToggles] = React.useState<Record<string, boolean>>({})
  const previousUnreadRef = React.useRef(0)
  const audioContextRef = React.useRef<AudioContext | null>(null)
  const userInteractedRef = React.useRef(false)

  // Fetch section toggles
  React.useEffect(() => {
    axios.get('/api/settings/section-toggles')
      .then(res => {
        const toggles: Record<string, boolean> = {}
        ;(res.data || []).forEach((t: { key: string; enabled: number }) => {
          toggles[t.key] = !!t.enabled
        })
        setSectionToggles(toggles)
      })
      .catch(() => {})
  }, [])

  const filteredNavigation = React.useMemo(
    () =>
      navigation.filter((item) => {
        if (item.name === "Chat" && !WHATSAPP_CHAT_ENABLED) return false
        if (item.adminOnly && !hasRole("admin")) return false
        if (item.anyPermissions && !item.anyPermissions.some((perm) => hasPermission(perm))) return false
        if (item.permission && !hasPermission(item.permission)) return false
        if (item.toggleKey && sectionToggles[item.toggleKey] === false) return false
        return true
      }).map((item) => {
        if (!item.items) return item
        return {
          ...item,
          items: item.items.filter((sub) => {
            if (sub.name === "Chat" && !WHATSAPP_CHAT_ENABLED) return false
            if (sub.adminOnly && !hasRole("admin")) return false
            if (sub.anyPermissions && !sub.anyPermissions.some((perm) => hasPermission(perm))) return false
            if (sub.permission && !hasPermission(sub.permission)) return false
            if (sub.toggleKey && sectionToggles[sub.toggleKey] === false) return false
            return true
          })
        }
      }),
    [hasPermission, hasRole, sectionToggles]
  )

  // Orden personalizado de las secciones (se guarda en este navegador)
  const ORDER_KEY = "sidebar-nav-order"
  const [navOrder, setNavOrder] = React.useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(ORDER_KEY) || "[]") } catch { return [] }
  })
  const [showOrderDialog, setShowOrderDialog] = React.useState(false)

  const orderedNavigation = React.useMemo(() => {
    if (!navOrder.length) return filteredNavigation
    const idx = new Map(navOrder.map((n, i) => [n, i]))
    return [...filteredNavigation].sort((a, b) => {
      const ai = idx.has(a.name) ? (idx.get(a.name) as number) : 1000 + navigation.findIndex((n) => n.name === a.name)
      const bi = idx.has(b.name) ? (idx.get(b.name) as number) : 1000 + navigation.findIndex((n) => n.name === b.name)
      return ai - bi
    })
  }, [filteredNavigation, navOrder])

  const moveSection = (name: string, dir: -1 | 1) => {
    const names = orderedNavigation.map((n) => n.name)
    const i = names.indexOf(name)
    const j = i + dir
    if (i < 0 || j < 0 || j >= names.length) return
    ;[names[i], names[j]] = [names[j], names[i]]
    setNavOrder(names)
    try { localStorage.setItem(ORDER_KEY, JSON.stringify(names)) } catch { /* sin storage */ }
  }

  const resetOrder = () => {
    setNavOrder([])
    try { localStorage.removeItem(ORDER_KEY) } catch { /* sin storage */ }
  }

  const canSeeChat = React.useMemo(() => {
    return filteredNavigation.some((item) => {
      if (item.name === "Chat") return true
      if (!item.items) return false
      return item.items.some((subItem) => subItem.name === "Chat")
    })
  }, [filteredNavigation])

  const toggleMenu = (name: string) => {
    setOpenMenus((prev) => ({
      ...prev,
      [name]: !prev[name],
    }))
  }

  const isMenuActive = React.useCallback(
    (item: NavigationItem) => {
      if (item.href && location.pathname.startsWith(item.href)) return true
      if (item.items) {
        return item.items.some((subItem) => location.pathname.startsWith(subItem.href))
      }
      return false
    },
    [location.pathname]
  )

  React.useEffect(() => {
    const activeMenu = filteredNavigation.find((item) => item.items && isMenuActive(item))
    if (activeMenu) {
      setOpenMenus((prev) => ({
        ...prev,
        [activeMenu.name]: true,
      }))
    }
  }, [filteredNavigation, isMenuActive])

  React.useEffect(() => {
    const visibleSubItems = filteredNavigation.flatMap((item) => {
      if (!item.items) return []
      return item.items.filter((subItem) => {
        if (subItem.adminOnly && !hasRole("admin")) return false
        if (subItem.hideForRoles && subItem.hideForRoles.some((role) => hasRole(role))) return false
        if (subItem.anyPermissions && !subItem.anyPermissions.some((perm) => hasPermission(perm))) return false
        if (subItem.permission && !hasPermission(subItem.permission)) return false
        return true
      })
    })

    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement
      ) {
        return
      }

      const key = event.key.toUpperCase()
      const subItem = visibleSubItems.find((item) => item.shortcut === key)
      if (subItem?.href) {
        event.preventDefault()
        navigate(subItem.href)
        return
      }

      const item = filteredNavigation.find((item) => item.shortcut === key)
      if (item?.href) {
        event.preventDefault()
        navigate(item.href)
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [filteredNavigation, hasPermission, hasRole, navigate])

  React.useEffect(() => {
    if (!canSeeChat) {
      setChatUnreadCount(0)
      previousUnreadRef.current = 0
      return
    }

    let cancelled = false
    let inFlight = false
    const AudioCtx = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext | undefined

    const unlockAudio = () => {
      userInteractedRef.current = true
      if (!AudioCtx) return
      if (!audioContextRef.current) {
        audioContextRef.current = new AudioCtx()
      }
      if (audioContextRef.current.state === "suspended") {
        audioContextRef.current.resume().catch(() => {})
      }
    }

    const playNotificationSound = () => {
      if (!AudioCtx || !userInteractedRef.current) return
      if (!audioContextRef.current) {
        audioContextRef.current = new AudioCtx()
      }
      const ctx = audioContextRef.current
      if (!ctx) return
      if (ctx.state === "suspended") {
        ctx.resume().catch(() => {})
      }

      const oscillator = ctx.createOscillator()
      const gain = ctx.createGain()
      oscillator.type = "sine"
      oscillator.frequency.setValueAtTime(880, ctx.currentTime)
      gain.gain.setValueAtTime(0.0001, ctx.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.06, ctx.currentTime + 0.01)
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.17)
      oscillator.connect(gain)
      gain.connect(ctx.destination)
      oscillator.start()
      oscillator.stop(ctx.currentTime + 0.17)
    }

    const fetchUnreadCount = async () => {
      if (inFlight) return
      inFlight = true
      try {
        const response = await axios.get("/api/whatsapp/chat/unread-count")
        const nextCount = Number(response?.data?.unread || 0)
        if (!cancelled) {
          const safeCount = Number.isFinite(nextCount) ? nextCount : 0
          if (safeCount > previousUnreadRef.current) {
            playNotificationSound()
          }
          previousUnreadRef.current = safeCount
          setChatUnreadCount(safeCount)
        }
      } catch (_error) {
        if (!cancelled) setChatUnreadCount(0)
      } finally {
        inFlight = false
      }
    }

    window.addEventListener("pointerdown", unlockAudio, { passive: true })
    window.addEventListener("keydown", unlockAudio)
    fetchUnreadCount()
    // Tiempo real: el contador se actualiza por socket; el interval es respaldo
    const timer = window.setInterval(fetchUnreadCount, 30000)
    const socket = getAppSocket()
    socket.on("chat:message", fetchUnreadCount)
    return () => {
      cancelled = true
      window.clearInterval(timer)
      socket.off("chat:message", fetchUnreadCount)
      window.removeEventListener("pointerdown", unlockAudio)
      window.removeEventListener("keydown", unlockAudio)
    }
  }, [canSeeChat])

  const userMeta = React.useMemo(() => (user ?? {}) as Record<string, any>, [user])

  const companyLabel =
    (userMeta.companyName as string | undefined) ??
    (userMeta.company?.name as string | undefined) ??
    (userMeta.organizationName as string | undefined) ??
    ""

  return (
    <SidebarRoot collapsible="icon" variant="floating" className="group/sidebar">
      <SidebarHeader className="p-2">
        <div className="flex items-center justify-center w-full">
          <NavLink to="/dashboard" className="flex items-center justify-center w-full p-1">
            {state === "collapsed" ? (
              <div className="flex items-center justify-center h-12 w-12">
                <img
                  src={isDarkMode ? getLogoDarkUrl() : getLogoUrl()}
                  onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
                    event.currentTarget.src = "/rocam-new-logo-2025.png"
                  }}
                  alt="Logo"
                  className="h-full w-full object-contain"
                  style={{ maxHeight: "48px", maxWidth: "48px" }}
                />
              </div>
            ) : (
              <img
                src={isDarkMode ? getLogoDarkUrl() : getLogoUrl()}
                onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
                  event.currentTarget.src = "/rocam-new-logo-2025.png"
                }}
                alt={companyLabel || "Logo"}
                className="h-8 w-auto object-contain"
              />
            )}
          </NavLink>
        </div>
      </SidebarHeader>

      <SidebarContent className="p-2">
        <SidebarMenu className="space-y-1">
          {orderedNavigation.map((item) => {
            const Icon = item.icon
            const isActive = isMenuActive(item)
            const isOpen = openMenus[item.name]

            if (item.items) {
              const visibleSubItems = item.items.filter((subItem) => {
                if (subItem.adminOnly && !hasRole("admin")) return false
                if (subItem.hideForRoles && subItem.hideForRoles.some((role) => hasRole(role))) return false
                if (subItem.anyPermissions && !subItem.anyPermissions.some((perm) => hasPermission(perm))) return false
                if (subItem.permission && !hasPermission(subItem.permission)) return false
                return true
              })

              if (!visibleSubItems.length) return null

              return (
                <SidebarMenuItem key={item.name}>
                  <SidebarMenuButton
                    onClick={() => toggleMenu(item.name)}
                    tooltip={{
                      children: <span>{item.name}</span>,
                      hidden: state !== "collapsed",
                    }}
                    isActive={isActive}
                    className="peer/menu-button cursor-pointer flex w-full items-center gap-2 overflow-hidden rounded-lg p-2 text-left outline-hidden transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground h-8 text-sm [&_svg]:text-foreground/70 hover:[&_svg]:text-foreground data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2!"
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="flex w-full items-center justify-between truncate group-data-[collapsible=icon]/sidebar:hidden">
                      <span className="truncate">{item.name}</span>
                      <ChevronRightIcon className={`h-4 w-4 transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`} />
                    </span>
                  </SidebarMenuButton>

                  {isOpen && (
                    <SidebarMenuSub>
                      {visibleSubItems.map((subItem) => (
                        <SidebarMenuSubItem key={subItem.href}>
                          <SidebarMenuSubButton asChild isActive={!subItem.external && location.pathname.startsWith(subItem.href)}>
                            {subItem.external ? (
                              <a href={subItem.href} target="_blank" rel="noopener noreferrer" className="flex w-full items-center justify-between gap-2">
                                <span className="truncate">{subItem.name}</span>
                                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 opacity-60 shrink-0" />
                              </a>
                            ) : (
                            <NavLink to={subItem.href} className="flex w-full items-center justify-between gap-2">
                              <span className="truncate">{subItem.name}</span>
                              {subItem.shortcut && (
                                <kbd className="pointer-events-none hidden select-none items-center px-1 text-xs font-bold font-sans bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 rounded-sm border xl:flex">
                                  {subItem.shortcut}
                                </kbd>
                              )}
                              {subItem.name === "Chat" && chatUnreadCount > 0 && (
                                <span className="inline-flex min-w-[18px] h-[18px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
                                  {chatUnreadCount > 99 ? "99+" : chatUnreadCount}
                                </span>
                              )}
                            </NavLink>
                            )}
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  )}
                </SidebarMenuItem>
              )
            }

            return (
              <SidebarMenuItem key={item.href}>
                <SidebarMenuButton
                  asChild
                  tooltip={{
                    children: (
                      <div className="flex items-center gap-4">
                        <span>{item.name}</span>
                        {item.shortcut && (
                          <kbd className="px-1 font-bold font-sans bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 rounded-sm border">
                            {item.shortcut}
                          </kbd>
                        )}
                      </div>
                    ),
                    hidden: state !== "collapsed",
                  }}
                  isActive={isActive}
                  className="peer/menu-button cursor-pointer flex w-full items-center gap-2 overflow-hidden rounded-lg p-2 text-left outline-hidden transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground h-8 text-sm [&_svg]:text-foreground/70 hover:[&_svg]:text-foreground data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2!"
                >
                  <NavLink to={item.href!} className="relative flex w-full items-center gap-2">
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="flex w-full items-center justify-between truncate group-data-[collapsible=icon]/sidebar:hidden">
                      <span className="truncate">{item.name}</span>
                      {item.shortcut && (
                        <kbd className="pointer-events-none hidden select-none items-center px-1 text-xs font-bold font-sans bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 rounded-sm border xl:flex">
                          {item.shortcut}
                        </kbd>
                      )}
                    </span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )
          })}
        </SidebarMenu>

        {state !== "collapsed" && (
          <button
            type="button"
            onClick={() => setShowOrderDialog(true)}
            className="mt-auto flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground/60 hover:text-foreground transition-colors"
          >
            <ArrowsUpDownIcon className="h-3.5 w-3.5" />
            Ordenar menú
          </button>
        )}
      </SidebarContent>

      <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Ordenar el menú</DialogTitle>
            <DialogDescription>
              Mové las secciones con las flechas. El orden se guarda en este navegador.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            {orderedNavigation.map((item, i) => {
              const Icon = item.icon
              return (
                <div key={item.name} className="flex items-center gap-2 rounded-lg border px-3 py-2">
                  <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-sm font-medium flex-1 truncate">{item.name}</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="size-7"
                    disabled={i === 0}
                    onClick={() => moveSection(item.name, -1)}
                  >
                    <ChevronUpIcon className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="size-7"
                    disabled={i === orderedNavigation.length - 1}
                    onClick={() => moveSection(item.name, 1)}
                  >
                    <ChevronDownIcon className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )
            })}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" onClick={resetOrder} disabled={!navOrder.length}>
              Restablecer
            </Button>
            <Button type="button" onClick={() => setShowOrderDialog(false)}>Listo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SidebarRail />
    </SidebarRoot>
  )
}

export default AppSidebar
